import React from "react";
import { createApi } from "../../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { GeneralInfoTab } from "../GeneralInfoTab";
import { SchedulesTab } from "../SchedulesTab";
import { getUpdatedSitesPageInfo } from "../../../AddSite/getUpdatedSitesPageInfo";
import { checkArmedStatus } from "./checkArmedStatus";
import handlePatchRecordingServerData from "./handlePatchRecordingServerData";
import { RecordingServerTab } from "../RecordingServerTab";
import { handlePatchCameraHealthData } from "./handlePatchCameraHealthData";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { handlePatchWebhooksData } from "./handlePatchWebhooksData";
import { CameraHealthTab } from "../CameraHealthTab";
import { WebhooksTab } from "../WebhooksTab";
import { getNumbersAfterAboutSite } from "./getNumbersAfterAboutSite";
import { useSiteAboutStore } from "../siteAboutStore/siteAboutStore";
import { EvalinkTab } from "../EvalinkTab";
import { WireGuardTab } from "../WireGuardTab";
import { WireGuardEditSession } from "../WireGuardTabEditMode";
import { handlePatchWireguardTunnels } from "./handlePatchWireguardTunnels";
import { WireGuardTunnel } from "./siteAboutInterfaces";
import { NavigateFunction } from "react-router-dom";
import { fetchAddCameraInfo } from "../../SitesPage/SitesPageUtils/fetchAddCameraInfo";
import { withSavedMotionStreamingAdvice } from "./motionStreamingAdvice";

export const shouldRefreshAddCameraInfoAfterSiteInfoSave = (
  patchObject: Record<string, unknown>
): boolean =>
  Object.prototype.hasOwnProperty.call(patchObject, "default_alarm");

/**
 * Runs only the Auto Patrol schedule API flow (sensitivity, health metrics, product metrics).
 * Calls onComplete when all async work finishes (or when no schedule changes to submit).
 */
export const saveAutoPatrolSchedulesOnly = async (
  navigate: NavigateFunction,
  onComplete?: () => void
): Promise<void> => {
  const {
    autoPatrolSchedulesUpdatedBoolean,
    updatedAutoPatrolScheduleIndexes,
    patchAutoPatrolSchedules,
    autoPatrolScheduleHealthchckMetricsUpdatedBoolean,
    updatedAutoPatrolScheduleHealthchckMetricsIds,
    autoPatrolScheduleHealthchckMetrics,
    patchAutoPatrolScheduleHealthchckMetrics,
    productMetricsWereUpdated,
    updatedProductMetricsIds,
    updateScheduleProducts,
    autoPatrolCameraProducts,
    setProductMetricsWereUpdated,
    setUpdatedProductMetricsIds,
  } = useSiteAboutStore.getState();

  const promises: Promise<void>[] = [];

  if (autoPatrolSchedulesUpdatedBoolean) {
    updatedAutoPatrolScheduleIndexes.forEach((index: any) => {
      promises.push(patchAutoPatrolSchedules(navigate, index));
    });
  }
  if (autoPatrolScheduleHealthchckMetricsUpdatedBoolean) {
    const arraysToPatch: any[] = [];
    updatedAutoPatrolScheduleHealthchckMetricsIds.forEach((id: any) => {
      const metricsForSchedule = autoPatrolScheduleHealthchckMetrics[id] || [];
      metricsForSchedule.forEach((metric: any) => {
        if (!metric?.id) {
          return;
        }
        arraysToPatch.push({
          id: metric.id,
          enabled: metric.enabled,
          finalIndex: false,
          minimum_resolution_height:
            metric?.metric_type?.value === "image_quality_check"
              ? metric.minimum_resolution_height
              : null,
        });
      });
    });
    if (arraysToPatch.length > 0) {
      arraysToPatch[arraysToPatch.length - 1].finalIndex = true;
    }
    arraysToPatch.forEach((array: any) => {
      promises.push(
        patchAutoPatrolScheduleHealthchckMetrics(
          navigate,
          array.id,
          array.enabled,
          array.finalIndex,
          array.minimum_resolution_height
        )
      );
    });
  }
  if (productMetricsWereUpdated) {
    Object.entries(updatedProductMetricsIds).forEach(
      ([scheduleIdStr, productIds]) => {
        const scheduleId = Number(scheduleIdStr);
        const scheduleProducts = autoPatrolCameraProducts[scheduleId] || [];
        productIds.forEach((productId: number) => {
          const product = scheduleProducts.find((p) => p.id === productId);
          if (product) {
            promises.push(
              updateScheduleProducts(
                productId,
                product.enabled,
                scheduleId,
                navigate
              )
            );
          }
        });
      }
    );
  }

  if (promises.length > 0) {
    Promise.all(promises).finally(() => {
      setProductMetricsWereUpdated(false);
      setUpdatedProductMetricsIds({});
      onComplete?.();
    });
  } else {
    onComplete?.();
  }
};

export const handleSaveChangesButtonClick = async (
  patchObject: any,
  setEditMode: any,
  setSiteInfo: any,
  setPatchObject: any,
  siteInfo: any,
  clickedTab: any,
  setActiveTab: any,
  vmsVariables: any,
  siteSchedules: any,
  setAllSiteSchedules: any,
  setBackdropLoadingScreenOn: any,
  setAlertState: any,
  setSyncNeeded: any,
  editMode: any,
  setSchedulesTabActive: any,
  setClickedTab: any,
  siteInfoChangesMade: any,
  setSiteInfoChangesMade: any,
  // scheduleChangesMade: any,
  setScheduleChangesMade: any,
  setDeleteScheduleDialogOpen: any,
  navigate: any,
  setSiteSchedules: any,
  setArmButtonDisabled: any,
  recordingServerChangesMade: any,
  recordingServerData: any,
  recordingServerPatchObject: any,
  setRecordingServerData: any,
  setRecordingServerPatchObject: any,
  setRecordingServerChangesMade: any,
  cameraHealthChangesMade: any,
  healthCheckData: any,
  setHealthCheckData: any,
  cameraHealthPatchObject: any,
  setCameraHealthPatchObject: any,
  setCameraHealthChangesMade: any,
  setArmedStatus: any,
  webhooksChangesMade: boolean,
  webhooksInfo: any,
  setWebhooksInfo: any,
  webhooksPatchObject: any,
  setWebhooksPatchObject: any,
  setWebhooksChangesMade: any,
  setDeactivateSiteInsteadDialogOpen: any,
  setActivateSiteWithScheduleDialogOpen: any,
  wireguardChangesMade: boolean,
  wireGuardEditSessionRef: React.MutableRefObject<WireGuardEditSession | null>,
  setWireguardTunnels: React.Dispatch<React.SetStateAction<WireGuardTunnel[]>>,
  setWireguardChangesMade: React.Dispatch<React.SetStateAction<boolean>>,
  wireguardTunnels: WireGuardTunnel[],
  wireguardTunnelsLoading: boolean,
  selectedWireguardTunnelId: number | null,
  setSelectedWireguardTunnelId: React.Dispatch<
    React.SetStateAction<number | null>
  >
) => {
  const {
    healthCheckMetricsUpdated,
    healthCheckMetricsData,
    patchHealthCheckMetricsData,
    getHealthCheckMetricsData,
    setScheduleGridEditable,
    scheduleChangesMade,
    patchSchedules,
    setBackdropLoadingScreenOnEditingSchedule,
    evalinkChangesMade,
    handlePatchEvalink,
  } = useSiteAboutStore.getState();

  //create a value anyHealthChceckMetricsUpdated that is true if any of the healthCheckMetricsUpdated values have a true value
  const anyHealthCheckMetricsUpdated = Object.values(
    healthCheckMetricsUpdated
  ).some((value) => value === true);

  const AxiosUI = createApi(``);
  setBackdropLoadingScreenOn(true);
  const retryFunction = () => {
    handleSaveChangesButtonClick(
      patchObject,
      setEditMode,
      setSiteInfo,
      setPatchObject,
      siteInfo,
      clickedTab,
      setActiveTab,
      vmsVariables,
      siteSchedules,
      setAllSiteSchedules,
      setBackdropLoadingScreenOn,
      setAlertState,
      setSyncNeeded,
      editMode,
      setSchedulesTabActive,
      setClickedTab,
      siteInfoChangesMade,
      setSiteInfoChangesMade,
      // scheduleChangesMade,
      setScheduleChangesMade,
      setDeleteScheduleDialogOpen,
      navigate,
      setSiteSchedules,
      setArmButtonDisabled,
      recordingServerChangesMade,
      recordingServerData,
      recordingServerPatchObject,
      setRecordingServerData,
      setRecordingServerPatchObject,
      setRecordingServerChangesMade,
      cameraHealthChangesMade,
      healthCheckData,
      setHealthCheckData,
      cameraHealthPatchObject,
      setCameraHealthPatchObject,
      setCameraHealthChangesMade,
      setArmedStatus,
      webhooksChangesMade,
      webhooksInfo,
      setWebhooksInfo,
      webhooksPatchObject,
      setWebhooksPatchObject,
      setWebhooksChangesMade,
      setDeactivateSiteInsteadDialogOpen,
      setActivateSiteWithScheduleDialogOpen,
      wireguardChangesMade,
      wireGuardEditSessionRef,
      setWireguardTunnels,
      setWireguardChangesMade,
      wireguardTunnels,
      wireguardTunnelsLoading,
      selectedWireguardTunnelId,
      setSelectedWireguardTunnelId
    );
  };
  saveAutoPatrolSchedulesOnly(navigate);
  if (siteInfoChangesMade) {
    await AxiosUI.patch(
      `customer/${getNumbersAfterAboutSite(location.href)}/about/`,
      patchObject
    ).then(
      (response: AxiosResponse) => {
        // The RTSP + motion advice (ENG-2828) depends on the saved protocol /
        // use_motion, so take it from the response rather than the draft.
        const savedSiteInfo = withSavedMotionStreamingAdvice(
          siteInfo,
          response?.data
        );
        if (savedSiteInfo !== siteInfo) {
          setSiteInfo(savedSiteInfo);
        }
        setSiteInfoChangesMade(false);
        setPatchObject({ name: siteInfo.name });
        setAlertState((previousState: any) => {
          return {
            ...previousState,
            changeSuccess: true,
            activateSuccess: false,
            deactivateSuccess: false,
            disarmSuccess: false,
            armSuccess: false,
            syncSuccess: false,
            cameraDeleteSuccess: false,
          };
        });
        setScheduleGridEditable(false);
        setEditMode(false);
        setSyncNeeded(true);
        setBackdropLoadingScreenOn(false);
        if (shouldRefreshAddCameraInfoAfterSiteInfoSave(patchObject)) {
          fetchAddCameraInfo(
            String(getNumbersAfterAboutSite(location.href)),
            navigate,
            { silent: true }
          );
        }
        if (localStorage.groupNameChange === "true") {
          localStorage.setItem("groupNameChange", "false");
          AxiosUI.get(
            `customer/${getNumbersAfterAboutSite(location.href)}/about/`
          ).then(
            (response: AxiosResponse) => {
              setSiteInfo(response.data);
              checkArmedStatus(
                response.data,
                setArmButtonDisabled,
                navigate,
                setSiteInfo,
                setArmedStatus
              );
              localStorage.setItem("siteInfo", JSON.stringify(response.data));
            },
            (reason: AxiosError) => {
              universalAPIErrorHandler(reason, navigate, retryFunction, () => {
                if (clickedTab === "one") {
                  setActiveTab(
                    <GeneralInfoTab
                      siteInfo={siteInfo}
                      setSiteInfo={setSiteInfo}
                      setActiveTab={setActiveTab}
                      vmsVariables={vmsVariables}
                    />
                  );
                } else if (clickedTab === "three") {
                  setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
                }
              });
            }
          );
          getUpdatedSitesPageInfo();
        }
        if (clickedTab === "one") {
          setActiveTab(
            <GeneralInfoTab
              siteInfo={savedSiteInfo}
              setSiteInfo={setSiteInfo}
              setActiveTab={setActiveTab}
              vmsVariables={vmsVariables}
            />
          );
        } else if (clickedTab === "three") {
          setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
        } else if (clickedTab === "five") {
          setActiveTab(
            <RecordingServerTab recordingServerData={recordingServerData} />
          );
        }
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setSiteInfo(JSON.parse(localStorage.siteInfo));
          setScheduleGridEditable(false);
          setEditMode(false);
          setBackdropLoadingScreenOn(false);
          if (clickedTab === "one") {
            setActiveTab(
              <GeneralInfoTab
                siteInfo={siteInfo}
                setSiteInfo={setSiteInfo}
                setActiveTab={setActiveTab}
                vmsVariables={vmsVariables}
              />
            );
          } else if (clickedTab === "three") {
            setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
          }
        });
      }
    );
  }
  if (scheduleChangesMade && clickedTab === "three") {
    setBackdropLoadingScreenOn(false);
    setBackdropLoadingScreenOnEditingSchedule(true);
    patchSchedules(setEditMode, setActiveTab, siteInfo, navigate);
    // if (handleCheckOverlappingSchedulesForSharedProducts(siteSchedules)) {
    //   alert(
    //     "Can not submit schedule changes due to overlapping products on flex schedules.  Please update the flex schdules so that no two schedules have the same product running simultaneously."
    //   );
    //   setBackdropLoadingScreenOn(false);
    // } else
    //   await submitScheduleChanges(
    //     siteSchedules,
    //     setAllSiteSchedules,
    //     setEditMode,
    //     setBackdropLoadingScreenOn,
    //     clickedTab,
    //     setActiveTab,
    //     siteInfo,
    //     vmsVariables,
    //     setDeleteScheduleDialogOpen,
    //     navigate,
    //     patchObject,
    //     setSiteInfo,
    //     setPatchObject,
    //     setAlertState,
    //     setSyncNeeded,
    //     editMode,
    //     setSchedulesTabActive,
    //     setClickedTab,
    //     siteInfoChangesMade,
    //     setSiteInfoChangesMade,
    //     scheduleChangesMade,
    //     setScheduleChangesMade,
    //     setSiteSchedules,
    //     setArmedStatus,
    //     setDeactivateSiteInsteadDialogOpen,
    //     setArmButtonDisabled,
    //     recordingServerChangesMade,
    //     recordingServerData,
    //     recordingServerPatchObject,
    //     setRecordingServerData,
    //     setRecordingServerPatchObject,
    //     setRecordingServerChangesMade,
    //     cameraHealthChangesMade,
    //     healthCheckData,
    //     setHealthCheckData,
    //     cameraHealthPatchObject,
    //     setCameraHealthPatchObject,
    //     setCameraHealthChangesMade,
    //     webhooksChangesMade,
    //     webhooksInfo,
    //     setWebhooksInfo,
    //     webhooksPatchObject,
    //     setWebhooksPatchObject,
    //     setWebhooksChangesMade,
    //     setActivateSiteWithScheduleDialogOpen
    //   );
  }
  if (recordingServerChangesMade) {
    handlePatchRecordingServerData(
      recordingServerData,
      recordingServerPatchObject,
      setRecordingServerData,
      setRecordingServerPatchObject,
      setRecordingServerChangesMade,
      setBackdropLoadingScreenOn,
      setEditMode,
      setActiveTab,
      setAlertState,
      navigate
    );
  }
  if (cameraHealthChangesMade) {
    handlePatchCameraHealthData(
      healthCheckData,
      setHealthCheckData,
      cameraHealthPatchObject,
      setCameraHealthPatchObject,
      setCameraHealthChangesMade,
      setActiveTab,
      setAlertState,
      setBackdropLoadingScreenOn,
      setEditMode,
      siteInfo,
      navigate
    );
  }
  if (webhooksChangesMade) {
    handlePatchWebhooksData(
      webhooksInfo,
      setWebhooksInfo,
      webhooksPatchObject,
      setWebhooksPatchObject,
      setWebhooksChangesMade,
      setActiveTab,
      setAlertState,
      setBackdropLoadingScreenOn,
      setEditMode,
      navigate
    );
  }
  if (evalinkChangesMade) {
    setBackdropLoadingScreenOn(true);
    handlePatchEvalink(
      navigate,
      setBackdropLoadingScreenOn,
      setEditMode,
      () => {
        setActiveTab(<EvalinkTab />);
      }
    );
  }
  if (wireguardChangesMade && clickedTab === "nine") {
    try {
      await handlePatchWireguardTunnels(
        wireGuardEditSessionRef.current,
        String(getNumbersAfterAboutSite(location.href)),
        setWireguardTunnels,
        setWireguardChangesMade,
        setEditMode,
        setActiveTab,
        setBackdropLoadingScreenOn,
        navigate,
        selectedWireguardTunnelId,
        setSelectedWireguardTunnelId
      );
    } finally {
      // WireGuard-only saves skip the aggregate branch below that clears the backdrop.
      // Always reset here so the main loader cannot stay open (e.g. after token-refresh
      // paths that return before the patch error callback runs).
      setBackdropLoadingScreenOn(false);
    }
    wireGuardEditSessionRef.current = null;
  }
  if (
    !siteInfoChangesMade &&
    !scheduleChangesMade &&
    !recordingServerChangesMade &&
    !cameraHealthChangesMade &&
    !webhooksChangesMade &&
    !anyHealthCheckMetricsUpdated &&
    !evalinkChangesMade &&
    !(wireguardChangesMade && clickedTab === "nine")
  ) {
    setBackdropLoadingScreenOn(false);
    if (clickedTab === "one") {
      setActiveTab(
        <GeneralInfoTab
          siteInfo={siteInfo}
          setSiteInfo={setSiteInfo}
          setActiveTab={setActiveTab}
          vmsVariables={vmsVariables}
        />
      );
    } else if (clickedTab === "three") {
      setScheduleGridEditable(false);
      setEditMode(false);
      setActiveTab(<SchedulesTab siteInfo={siteInfo} />);
    } else if (clickedTab === "five") {
      setActiveTab(
        <RecordingServerTab recordingServerData={recordingServerData} />
      );
    } else if (clickedTab === "six") {
      setActiveTab(
        <CameraHealthTab
          healthCheckData={healthCheckData}
          siteInfo={siteInfo}
        />
      );
    } else if (clickedTab === "seven") {
      setActiveTab(<WebhooksTab webhooksInfo={webhooksInfo} />);
    } else if (clickedTab === "eight") {
      setActiveTab(<EvalinkTab />);
    } else if (clickedTab === "nine") {
      setActiveTab(
        <WireGuardTab
          wireguardTunnels={wireguardTunnels}
          wireguardTunnelsLoading={wireguardTunnelsLoading}
          selectedTunnelId={selectedWireguardTunnelId}
          onSelectTunnelId={(id) => setSelectedWireguardTunnelId(id)}
        />
      );
    }
  }
  if (anyHealthCheckMetricsUpdated) {
    const patchPromises = Object.keys(healthCheckMetricsUpdated)
      .filter(
        (key) =>
          healthCheckMetricsUpdated[
            key as keyof typeof healthCheckMetricsUpdated
          ] === true
      )
      .map((key) => {
        return patchHealthCheckMetricsData(
          navigate,
          healthCheckMetricsData[key as keyof typeof healthCheckMetricsData].id,
          {
            enabled:
              healthCheckMetricsData[key as keyof typeof healthCheckMetricsData]
                .enabled,
            alert_emails:
              healthCheckMetricsData[key as keyof typeof healthCheckMetricsData]
                .alert_emails,
          },
          key
        );
      });

    try {
      await Promise.allSettled(patchPromises);

      await getHealthCheckMetricsData(
        navigate,
        getNumbersAfterAboutSite(location.href)
      );
      setActiveTab(
        <CameraHealthTab
          healthCheckData={healthCheckData}
          siteInfo={siteInfo}
        />
      );
      setBackdropLoadingScreenOn(false);
      setEditMode(false);
      setScheduleGridEditable(false);
    } catch (error) {
      setActiveTab(
        <CameraHealthTab
          healthCheckData={healthCheckData}
          siteInfo={siteInfo}
        />
      );
      setBackdropLoadingScreenOn(false);
      setEditMode(true);
      setScheduleGridEditable(false);
    }
  }
};
